/**
 * Skjermer: new screen, pairing, the fleet, and editing one screen.
 * CA-36, CA-58, CA-59, CA-60.
 *
 * - The list is the API's (fault 52: it was the mockup's four screens while
 *   the database held five others). A dark screen is named, and says how long.
 * - «Ny skjerm» mints the screen and says in three steps what to do with the
 *   code; the pairing field takes SIX digits.
 * - The editor offers only slides a screen can show. Neither janazah nor a
 *   live broadcast is offered: both take over every screen whatever is
 *   configured, and a checkbox for either would be a promise the platform
 *   overrules (fault 106). `mode` is not offered either: nothing reads it yet.
 * - Unticking «Overstyr» sends `config: null` (inherit the group); `{}` would
 *   mean "show nothing", which the API refuses and nobody should need to know.
 * - «Fjernstyr» (A2, 2026-10-24; capability screen_remote, permission screens.remote): a banner, an announcement or a
 *   silent screen on one screen or a group for a few minutes, and back to normal. The app's staff area is the main
 *   place for this (staff stand in the hall with a phone); this card is the same thing at the desk.
 */
import React, { useRef, useState } from 'react';
import { API_ORIGIN, can, errText, post, send, type Body } from '../lib/api';
import { useLoad, useMsg, useToastOn } from '../lib/hooks';
import { useNav } from '../lib/nav';
import { Body as CardBody, Btn, Card, Field, LoadError, Loading, Msg, Tag, TwoStep, type TagTone, CopyButton } from '../components/ui';
import { FormDialog } from '../components/dialog';

const SLIDE_NO: Record<string, string> = {
  prayer_grid: 'Bønnetider', event: 'Neste arrangement', notice: 'Kunngjøringer', banner: 'Bannere',
  jumua: "Jumu'ah-plakat (fredager)", ramadan: 'Ramadan-plakat',
  // Innsamling (2026-10-29): only drawn while the mosque has a running campaign (or one closed in the last 14 days).
  campaign: 'Innsamlinger',
};
const SOURCE_NO: Record<string, string> = { screen: 'satt på denne skjermen', group: 'arvet fra gruppen', default: 'standard' };
const MODE: Record<string, [TagTone, string]> = { broadcast: ['green', 'Kringkasting'], own: ['gold', 'Egen visning'] };

/**
 * The address a television opens: the API's own /tv/ page. The short one,
 * …/tv/1234, when the screen has a number (every screen does since
 * 2026-09-27) — it is typed on a TV remote. The server sends it on to the
 * long address with the screen's id, which still works and is the fallback.
 */
export const tvAddress = (id: string, shortCode?: string | null) =>
  shortCode ? API_ORIGIN + '/tv/' + shortCode : API_ORIGIN + '/tv/?screen=' + encodeURIComponent(id);
/** What to type: without https://, which every TV browser adds by itself. */
const typed = (url: string) => url.replace(/^https?:\/\//, '');

/**
 * One screen's TV address, with a way to copy it.
 *
 * Every row has one, paired or not. Pairing is a one-off; needing the address
 * again is not — a television is replaced, its browser data is cleared, or
 * somebody is setting the same screen up in a second room. Opening this
 * address again restores that same screen, because the screen's id is in it.
 */
function Address({ id, shortCode, onMessage }: { id: string; shortCode?: string | null; onMessage: (m: string) => void }) {
  const ref = useRef<HTMLElement>(null);
  const url = tvAddress(id, shortCode);
  return (
    <div className="sub screen-addr">
      <span>TV-adresse: </span>
      <code ref={ref}>{typed(url)}</code>{' '}
      <CopyButton value={url} label="Kopier" selectRef={ref} onMessage={onMessage} />
    </div>
  );
}

function quietText(h: Body) {
  const m = h.minutesSinceSeen;
  if (m === null || m === undefined) return 'aldri sett';
  if (m < 1) return 'sist sett akkurat nå';
  if (m < 60) return 'sist sett for ' + m + ' min siden';
  return 'sist sett for ' + Math.floor(m / 60) + ' t ' + (m % 60) + ' min siden';
}

export function Screens() {
  const { go, caps } = useNav();
  const { data, error, reload } = useLoad<Body>('/screens');
  const [fresh, setFresh] = useState<{ id: string; code: string; shortCode?: string | null } | null>(null);
  const [freshErr, setFreshErr] = useState('');
  const [copyMsg, setCopyMsg] = useState('');
  const freshUrlRef = useRef<HTMLElement>(null);
  const [pair, setPair] = useState({ code: '', name: '', group: '' });
  const [pairMsg, sayPair] = useMsg();
  const [editing, setEditing] = useState<string | null>(null);
  const [listMsg, setListMsg] = useState('');
  useToastOn(listMsg, 'ok');
  useToastOn(freshErr, 'err');
  useToastOn(copyMsg, copyMsg.startsWith('Adressen er kopiert') ? 'ok' : '');
  const nameRef = React.useRef<HTMLInputElement>(null);
  // Owner 2026-10-23: connecting a screen happens in a popup. «+ Ny skjerm» makes the screen and opens it with the
  // three steps; «Koble til med kode» opens it empty, for a TV that already shows a code. The result stays on the page.
  const [pairing, setPairing] = useState(false);

  if (error) return <LoadError error={error} retry={reload} />;
  if (!data) return <Loading />;
  const list: Body[] = data.items || [];
  const paired = list.filter((s) => s.health.status !== 'unpaired');

  return (
    <>
      <Card title="Koble til ny skjerm" hint="Klikk «Ny skjerm». Du får en kort adresse, som …/tv/1234, og en sekssifret kode. Skriv adressen i nettleseren på TV-en — den viser den samme koden. Skriv et navn på skjermen her og klikk «Koble til». Ingen innlogging på fjernkontrollen.">
        <CardBody>
          <div className="new-row" style={{ justifyContent: 'flex-start' }}><Btn small={false} onClick={async () => {
            setFreshErr(''); setCopyMsg(''); sayPair(''); setPairing(true);
            const r = await post('/screens/new');
            if (!r.ok) { setFresh(null); setFreshErr(errText(r.body)); return; }
            setFresh({ id: r.body.id, code: r.body.pairingCode, shortCode: r.body.shortCode });
            setPair({ ...pair, code: r.body.pairingCode });
            await reload();
            setTimeout(() => nameRef.current?.focus(), 0);
          }}>+ Ny skjerm</Btn>
            <Btn ghost small={false} onClick={() => { setFresh(null); setFreshErr(''); sayPair(''); setPairing(true); }}>Koble til med kode</Btn></div>
          {!pairing && <Msg text={pairMsg.text} tone={pairMsg.tone} />}
          <FormDialog open={pairing} title="Koble til ny skjerm" dirty={Boolean(pair.code.trim() || pair.name.trim())}
            onClose={() => { setPairing(false); setFresh(null); setFreshErr(''); setPair({ code: '', name: '', group: '' }); sayPair(''); }}>
          {fresh && (
            <div className="newscr" role="status">
              <div><b>Ny skjerm er klar.</b> Koden er <span className="code">{fresh.code}</span></div>
              <ol>
                <li>Skriv denne adressen i nettleseren på TV-en:<br /><code id="scr-new-url" ref={freshUrlRef}>{typed(tvAddress(fresh.id, fresh.shortCode))}</code>{' '}
                  {/* The same control as every row in the list below — the
                      address is not only copyable in the minute after it was
                      made (Daniyal, 2026-09-20). */}
                  <CopyButton value={tvAddress(fresh.id, fresh.shortCode)} label="Kopier adressen"
                              selectRef={freshUrlRef} onMessage={setCopyMsg} /></li>
                <li>TV-en viser koden <b>{fresh.code}</b>. Står den samme koden der, er det riktig skjerm.</li>
                <li>Skriv et navn på skjermen under — for eksempel «Inngang» — og klikk «Koble til». Koden er fylt inn for deg. TV-en begynner å vise innhold av seg selv.</li>
              </ol>
            </div>
          )}
          <div className="row" style={{ alignItems: 'flex-end', marginTop: 14 }}>
            <Field label="Kode fra TV-en" className="field half" style={{ maxWidth: 230 }}>{(id) =>
              <input id={id} inputMode="numeric" autoComplete="off" placeholder="000000" maxLength={6} value={pair.code}
                     style={{ fontSize: 26, letterSpacing: 6, textAlign: 'center', fontFamily: 'var(--font-head)' }}
                     onChange={(e) => setPair({ ...pair, code: e.target.value })} />}</Field>
            <Field label="Navn på skjerm" required className="field half">{(id) =>
              <input id={id} ref={nameRef} placeholder="F.eks. Inngang, Hovedsalen" value={pair.name} onChange={(e) => setPair({ ...pair, name: e.target.value })} />}</Field>
            <Field label="Gruppe" className="field half">{(id) =>
              <select id={id} value={pair.group} onChange={(e) => setPair({ ...pair, group: e.target.value })}>
                <option value="">Standardgruppe</option>
                {(data.groups || []).map((g: Body) => <option key={g.id} value={g.id}>{g.name}</option>)}
              </select>}</Field>
            <div className="field"><Btn onClick={async () => {
              const code = pair.code.replace(/\D/g, '');
              if (code.length !== 6) return sayPair('Koden på skjermen har seks sifre.', 'err');
              const r = await post('/screens/pair', { code, name: pair.name.trim(), groupId: pair.group || null });
              if (r.ok && r.body.screen) {
                sayPair((r.body.message || 'Skjermen er koblet til.') + ' TV-en viser innhold ved neste oppdatering (innen et halvt minutt).', 'ok');
                setFresh(null); setPair({ code: '', name: '', group: '' }); setPairing(false); await reload();
              } else sayPair(errText(r.body), 'err');
            }}>Koble til</Btn></div>
          </div>
          <Msg text={pairMsg.text} tone={pairMsg.tone} />
          </FormDialog>
        </CardBody>
      </Card>

      <Card title={'Registrerte skjermer · ' + paired.length}>
        {data.allDarkNote_no && <div className="card-body"><div className="warnbox">{data.allDarkNote_no}</div></div>}
        {list.length ? list.map((s) => {
          const h = s.health || {};
          const dark = h.status === 'down', unpaired = h.status === 'unpaired';
          const [tone, label]: [TagTone, string] = unpaired ? ['grey', 'Ikke koblet'] : dark ? ['red', 'Nede']
            : h.detail === 'unstable' ? ['gold', 'Ustabil'] : (MODE[s.mode] || MODE.broadcast);
          return (
            <React.Fragment key={s.id}>
              <div className="screenrow">
                <span className={'dotstat ' + (unpaired ? '' : dark ? 'down' : 'up')} aria-hidden="true" />
                <div><div className="nm">{s.name || 'uten navn'}</div>
                  <div className="sub" style={dark ? { color: 'var(--danger)' } : undefined}>
                    {unpaired ? <>Venter på kobling · kode på skjermen: <b>{s.pairingCode || '—'}</b></>
                      : [s.group || 'Standardgruppe', quietText(h)].join(' · ')}</div>
                  {/* The TV address, on EVERY row and copyable.  Daniyal,
                      2026-09-20: it was shown only while the page still
                      remembered having just created the screen, and for a
                      screen already in this list it was not shown at all —
                      so the day a TV was wiped or replaced there was no way
                      to get its address back out of the panel. It is the
                      durable link: opening it again restores that same
                      screen, because the id is in it. */}
                  <Address id={s.id} shortCode={s.shortCode} onMessage={setListMsg} />
                  {s.advice_no && <div className="sub">{s.advice_no}</div>}
                </div>
                <div className="sp" /><Tag tone={tone}>{label}</Tag>
                {!unpaired && <Btn ghost aria-expanded={editing === s.id} onClick={() => { setListMsg(''); setEditing(editing === s.id ? null : s.id); }}>{editing === s.id ? 'Lukk' : 'Endre'}</Btn>}
              </div>
              {editing === s.id && <Editor s={s} groups={data.groups || []} onCancel={() => setEditing(null)}
                                           onSaved={async (m) => { setEditing(null); setListMsg(m); await reload(); }} />}
            </React.Fragment>
          );
        }) : <div className="card-body">Ingen skjermer er registrert ennå.</div>}
        <CardBody style={{ borderTop: '1px solid var(--hairline)' }}>
          <span className="field note">Skjermene henter seg inn igjen selv etter strømbrudd og nettbrudd. Blir en skjerm borte, varsles teknisk kontakt automatisk — ingen trenger å gå runden i bygget om morgenen.</span>
        </CardBody>
      </Card>

      {(caps || []).includes('screen_remote') && can('screens.remote') && <Remote />}

      <ScreenGroups onChanged={reload} />
      <Card title="Innhold på skjermene" hint="Det meste endrer du ved å publisere, ikke ved å styre hver skjerm: publiser et banner, og alle skjermene viser det. Skal én skjerm eller én gruppe vise noe annet — «kun bønnetider» i kafeteriaen — klikker du «Endre» på skjermen i listen over.">
        <CardBody>
          <ul className="rules">
            <li><b>Et banner på alle skjermer:</b> lag og publiser det under Bannere. <Btn ghost onClick={() => go('banners')}>Åpne Bannere</Btn></li>
            <li><b>Direktesending:</b> sett arrangementet til DIREKTE under Arrangementer — da tar sendingen over alle skjermene. <Btn ghost onClick={() => go('events')}>Åpne Arrangementer</Btn></li>
            <li><b>Janazah:</b> kunngjøringen tar over alle skjermene til bønnen er over. <Btn ghost onClick={() => go('notices')}>Åpne Varsler</Btn></li>
            <li><b>Kunngjøringer og nyhetslinjen:</b> skriv en kunngjøring under Varsler og velg om den vises på skjermene, i appen eller begge steder. Skjermene viser den i nyhetslinjen nederst og som eget bilde. Nyhetslinjen slår du av per skjerm eller gruppe («Endre» over).</li>
            <li><b>Bare noen skjermer:</b> under «Vises på» på et banner, en kunngjøring eller et arrangement velger du grupper eller enkeltskjermer. Uten valg vises det på alle.</li>
          </ul>
          <span className="field note" style={{ display: 'block', marginTop: 8 }}>En janazah tar over alle skjermene uansett hva som er valgt på den enkelte skjermen. Bare en som kan godkjenne kunngjøringer kan begrense den til noen skjermer — og finnes ikke de valgte skjermene lenger, vises den på alle.</span>
        </CardBody>
      </Card>
    </>
  );
}

const REMOTE_KIND: Record<string, string> = { banner: 'Banner', notice: 'Kunngjøring', silence: 'Stille skjerm', normal: 'Tilbake til normal' };
const REMOTE_MINUTES = [5, 10, 30, 60];

/**
 * «Fjernstyr» (A2): what runs on the walls from the phone right now, and a small form to send one more. The TV picks it
 * up on its next poll (within half a minute) and ends it by itself when the minutes are up.
 */
function Remote() {
  const { data, error, reload } = useLoad<Body>('/screens/remote');
  const [target, setTarget] = useState('');
  const [kind, setKind] = useState('silence');
  const [ref, setRef] = useState('');
  const [text, setText] = useState('');
  const [minutes, setMinutes] = useState(10);
  const [msg, say] = useMsg();
  const [busy, setBusy] = useState(false);
  if (error) return <Card title="Fjernstyr"><LoadError error={error} retry={reload} /></Card>;
  if (!data) return <Card title="Fjernstyr"><Loading /></Card>;
  const screens: Body[] = data.screens || [], groups: Body[] = data.groups || [], overrides: Body[] = data.overrides || [];
  const nameOf = (o: Body) => o.targetKind === 'group'
    ? 'Gruppen ' + ((groups.find((g) => g.id === o.targetId) || {}).name || '—')
    : ((screens.find((x) => x.id === o.targetId) || {}).name || 'Skjerm');
  const options: Body[] = kind === 'banner' ? data.banners || [] : kind === 'notice' ? data.notices || [] : [];
  const sendIt = async () => {
    if (!target) return say('Velg en skjerm eller en gruppe.', 'err');
    const [targetKind, targetId] = target.split(':');
    const body: Body = { targetKind, targetId, kind };
    if (kind === 'banner' || kind === 'notice') { if (!ref) return say(kind === 'banner' ? 'Velg et banner.' : 'Velg en kunngjøring.', 'err'); body.refId = ref; }
    if (kind === 'silence') body.text = text.trim() || null;
    if (kind !== 'normal') body.minutes = minutes;
    setBusy(true);
    const r = await post('/screens/remote', body);
    setBusy(false);
    if (r.ok) { say(r.body.message_no || 'Sendt.', 'ok'); setRef(''); await reload(); } else say(errText(r.body), 'err');
  };
  return (
    <Card title="Fjernstyr" hint="Vis et banner, en kunngjøring eller en stille skjerm på én skjerm eller en gruppe i noen minutter. Skjermen viser det ved neste oppdatering og går tilbake til vanlig visning av seg selv. En janazah går alltid foran.">
      {overrides.length ? overrides.map((o) => (
        <div className="screenrow" key={o.id}>
          <span className="dotstat up" aria-hidden="true" />
          <div><div className="nm">{nameOf(o)}: {REMOTE_KIND[o.kind] || o.kind}{o.title ? ' — ' + o.title : o.kind === 'silence' && o.text ? ' — «' + o.text + '»' : ''}</div>
            <div className="sub">{o.remainingMinutes} min igjen{o.createdByName ? ' · sendt av ' + o.createdByName : ''}</div></div>
          <div className="sp" />
          <Btn ghost onClick={async () => { const r = await post('/screens/remote/' + encodeURIComponent(o.id) + '/cancel', {});
            if (r.ok) { say(r.body.message_no || 'Avsluttet.', 'ok'); await reload(); } else say(errText(r.body), 'err'); }}>Avslutt</Btn>
        </div>
      )) : <div className="card-body"><span className="field note">Ingen skjerm fjernstyres nå.</span></div>}
      <CardBody style={{ borderTop: '1px solid var(--hairline)' }}>
        <div className="row" style={{ alignItems: 'flex-end' }}>
          <Field label="Skjerm eller gruppe" className="field half">{(id) =>
            <select id={id} value={target} onChange={(e) => setTarget(e.target.value)}>
              <option value="">Velg …</option>
              <optgroup label="Skjermer">{screens.map((x) => <option key={x.id} value={'screen:' + x.id}>{x.name}{x.online ? '' : ' (nede)'}</option>)}</optgroup>
              <optgroup label="Grupper">{groups.map((g) => <option key={g.id} value={'group:' + g.id}>{g.name}</option>)}</optgroup>
            </select>}</Field>
          <Field label="Vis" className="field half">{(id) =>
            <select id={id} value={kind} onChange={(e) => { setKind(e.target.value); setRef(''); }}>
              {Object.entries(REMOTE_KIND).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>}</Field>
          {(kind === 'banner' || kind === 'notice') && <Field label={kind === 'banner' ? 'Banner' : 'Kunngjøring'} className="field half">{(id) =>
            <select id={id} value={ref} onChange={(e) => setRef(e.target.value)}>
              <option value="">{options.length ? 'Velg …' : 'Ingen publiserte'}</option>
              {options.map((x) => <option key={x.id} value={x.id}>{x.title}</option>)}
            </select>}</Field>}
          {kind === 'silence' && <Field label="Tekst på skjermen" className="field half" note={'Høyst ' + (data.textMax || 80) + ' tegn.'}>{(id, d) =>
            <input id={id} aria-describedby={d} maxLength={data.textMax || 80} placeholder={data.silenceText_no || 'Vennligst stillhet'} value={text} onChange={(e) => setText(e.target.value)} />}</Field>}
          {kind !== 'normal' && <Field label="Minutter" className="field" style={{ maxWidth: 130 }}>{(id) =>
            <select id={id} value={minutes} onChange={(e) => setMinutes(Number(e.target.value))}>
              {REMOTE_MINUTES.map((m) => <option key={m} value={m}>{m} min</option>)}
            </select>}</Field>}
          <div className="field"><Btn disabled={busy} onClick={sendIt}>{kind === 'normal' ? 'Tilbake til normal' : 'Send til skjermen'}</Btn></div>
        </div>
        <Msg text={msg.text} tone={msg.tone} />
      </CardBody>
    </Card>
  );
}

function Editor({ s, groups, onCancel, onSaved }: { s: Body; groups: Body[]; onCancel: () => void; onSaved: (msg: string) => void }) {
  const cfg = s.config || null, disp = s.display || {};
  const [name, setName] = useState<string>(s.name || '');
  const [group, setGroup] = useState<string>(s.groupId || 'all');
  const [own, setOwn] = useState(Boolean(cfg));
  const [slides, setSlides] = useState<string[]>(cfg && Array.isArray(cfg.slides) ? cfg.slides : Array.isArray(disp.slides) ? disp.slides : []);
  const [dwell, setDwell] = useState<number>((cfg && cfg.dwellSeconds) || disp.dwellSeconds || 12);
  // Split (default): the prayer times stay on one half, the rest rotate on the other.
  const [layout, setLayout] = useState<string>((cfg && cfg.layout) || (disp.source && disp.source.layout !== 'default' && disp.layout) || 'split');
  // The news bar (2026-10-21): on unless this screen, or its group, switched it off.
  const [newsBar, setNewsBar] = useState<boolean>(cfg && typeof cfg.newsBar === 'boolean' ? cfg.newsBar : disp.newsBar !== false);
  // Sound on a live broadcast (2026-10-25): off unless this screen, or its group, switched it on.
  const [liveSound, setLiveSound] = useState<boolean>(cfg && typeof cfg.liveSound === 'boolean' ? cfg.liveSound : disp.liveSound === true);
  const [msg, say] = useMsg();
  const [busy, setBusy] = useState(false);
  const where = SOURCE_NO[(disp.source || {}).slides] || SOURCE_NO.default;
  return (
    <div className="scr-edit">
      <div className="row" style={{ alignItems: 'flex-end' }}>
        <Field label="Navn på skjermen" className="field half">{(id) => <input id={id} value={name} placeholder="F.eks. Inngang" onChange={(e) => setName(e.target.value)} />}</Field>
        <Field label="Gruppe" className="field half" note="Skjermen viser det gruppen viser, med mindre du overstyrer under.">{(id, d) =>
          <select id={id} aria-describedby={d} value={group} onChange={(e) => setGroup(e.target.value)}>
            <option value="all">Standardgruppe</option>{groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
          </select>}</Field>
      </div>
      <div className="field" style={{ marginTop: 6 }}>
        <label className="chk"><input type="checkbox" checked={own} onChange={(e) => setOwn(e.target.checked)} /> Overstyr innholdet for denne skjermen</label>
        <span className="note">Uten dette følger skjermen gruppen sin. Innholdet er nå {where}.</span>
      </div>
      <fieldset className="se-body" disabled={!own}>
        <div className="field"><span id={'se-slides-' + s.id} style={{ display: 'block', fontFamily: 'var(--font-form)', fontSize: 13, marginBottom: 5 }}>Hva skjermen viser</span>
          <div className="se-slides" role="group" aria-labelledby={'se-slides-' + s.id}>
            {Object.entries(SLIDE_NO).map(([k, label]) => (
              <label key={k} className="chk"><input type="checkbox" checked={slides.includes(k)}
                onChange={(e) => setSlides(e.target.checked ? [...slides, k] : slides.filter((x) => x !== k))} /> {label}</label>))}
          </div>
          <span className="note">Minst én. En skjerm som ikke viser noe ser ødelagt ut i en korridor.</span>
        </div>
        <Field label="Oppsett" className="field half" style={{ maxWidth: 420 }} note="Delt: bønnetidene står fast på den ene halvdelen, og bannere, arrangementer og kunngjøringer skifter på den andre. Janazah og direktesending fyller alltid hele skjermen.">{(id, d) =>
          <select id={id} aria-describedby={d} value={layout} onChange={(e) => setLayout(e.target.value)}>
            <option value="split">Delt – bønnetider + bannere</option>
            <option value="full">Hele skjermen – ett bilde om gangen</option>
          </select>}</Field>
        <Field label="Visningstid per bilde (sekunder)" className="field half" style={{ maxWidth: 230 }} note="3–120. En korridor folk går forbi tåler kortere enn en sal folk sitter i.">{(id, d) =>
          <input id={id} aria-describedby={d} type="number" min={3} max={120} value={dwell} onChange={(e) => setDwell(Number(e.target.value))} />}</Field>
        <div className="field">
          <label className="chk"><input type="checkbox" checked={newsBar} onChange={(e) => setNewsBar(e.target.checked)} /> Nyhetslinje nederst på skjermen</label>
          <span className="note">Linjen som går sakte langs bunnen med kunngjøringene, de neste arrangementene og kommende Jumu'ah. Slå den av på en skjerm som ikke skal bevege seg — i en sal folk sitter i. Hvilke kunngjøringer den viser velger du under Varsler.</span>
        </div>
        <div className="field">
          <label className="chk"><input type="checkbox" checked={liveSound} onChange={(e) => setLiveSound(e.target.checked)} /> Lyd på direktesending</label>
          <span className="note">Når moskeen sender direkte på YouTube, spiller skjermen sendingen. Uten lyd som standard — slå på for skjermen i salen der folk lytter. Noen nettlesere spiller likevel uten lyd til noen har trykket på skjermen.</span>
        </div>
      </fieldset>
      <div className="row" style={{ marginTop: 10 }}>
        <Btn disabled={busy} onClick={async () => {
          setBusy(true); say('');
          const body = { name: name.trim(), groupId: group, config: own ? { slides: Object.keys(SLIDE_NO).filter((k) => slides.includes(k)), dwellSeconds: dwell, layout, newsBar, liveSound } : null };
          const r = await send('/screens/' + encodeURIComponent(s.id), { method: 'PATCH', body: JSON.stringify(body) });
          setBusy(false);
          if (!r.ok) return say(errText(r.body), 'err');
          onSaved(r.body.message_no || 'Lagret.');
        }}>Lagre</Btn>
        <Btn ghost onClick={onCancel}>Avbryt</Btn>
      </div>
      <Msg text={msg.text} tone={msg.tone} />
    </div>
  );
}

/** Screen groups (OA-032): rename, configure, and choose which screens belong — several at a time. */
function ScreenGroups({ onChanged }: { onChanged: () => void }) {
  const { data, error, reload } = useLoad<Body>('/screen-groups');
  const [msg, say] = useMsg();
  const [name, setName] = useState('');
  const [adding, setAdding] = useState(false);   // owner 2026-10-23: «+ Ny gruppe» opens a popup
  if (error) return <LoadError error={error} retry={reload} />;
  if (!data) return <Loading />;
  const all: Body[] = [...(data.items || []).flatMap((g: Body) => g.screens.map((s: Body) => ({ ...s, group: g.id }))), ...(data.ungrouped || []).map((s: Body) => ({ ...s, group: null }))];
  const done = (r: { ok: boolean; body: Body }, text: string) => { say(r.ok ? text : errText(r.body), r.ok ? 'ok' : 'err'); if (r.ok) { reload(); onChanged(); } };
  return (
    <Card title="Skjermgrupper" hint="En gruppe viser det samme på alle sine skjermer. Endringer når skjermene ved neste oppdatering. En skjerm som tas ut av en gruppe, går til standardgruppen — den blir aldri mørk.">
      <div className="new-row"><Btn small={false} onClick={() => { say(''); setAdding(true); }}>+ Ny gruppe</Btn></div>
      {!adding && <Msg text={msg.text} tone={msg.tone} />}
      {(data.items || []).map((g: Body) => (
        <details key={g.id} className="group-edit">
          <summary><b>{g.name}</b> · {g.screens.length} skjerm(er){g.config && g.config.layout ? ` · ${g.config.layout === 'full' ? 'hele skjermen' : 'delt'}` : ''}</summary>
          <CardBody>
            <Field label="Navn">{(id) => <input id={id} defaultValue={g.name} onBlur={async (e) => {
              const v = e.target.value.trim(); if (v && v !== g.name) done(await send(`/screen-groups/${g.id}`, { method: 'PATCH', body: JSON.stringify({ name: v }) }), 'Navnet er endret.'); }} />}</Field>
            <div className="row">
              <Field label="Visningstid (sekunder)" className="field half">{(id) => <input id={id} type="number" min={3} max={120} defaultValue={(g.config && g.config.dwellSeconds) || ''}
                onBlur={async (e) => { const n = Number(e.target.value); if (n) done(await send(`/screen-groups/${g.id}`, { method: 'PATCH', body: JSON.stringify({ config: { ...(g.config || {}), dwellSeconds: n } }) }), 'Lagret.'); }} />}</Field>
              <Field label="Oppsett" className="field half">{(id) => (
                <select id={id} defaultValue={(g.config && g.config.layout) || ''} onChange={async (e) => {
                  const cfg = { ...(g.config || {}) }; if (e.target.value) cfg.layout = e.target.value; else delete cfg.layout;
                  done(await send(`/screen-groups/${g.id}`, { method: 'PATCH', body: JSON.stringify({ config: Object.keys(cfg).length ? cfg : null }) }), 'Lagret.'); }}>
                  <option value="">Standard</option><option value="split">Bønnetider + bannere</option><option value="full">Hele skjermen</option>
                </select>)}</Field>
              <Field label="Nyhetslinje" className="field half" note="Linjen nederst med kunngjøringer, arrangementer og Jumu'ah. Standard: på.">{(id, d) => (
                <select id={id} aria-describedby={d} defaultValue={g.config && typeof g.config.newsBar === 'boolean' ? String(g.config.newsBar) : ''} onChange={async (e) => {
                  const cfg = { ...(g.config || {}) }; if (e.target.value) cfg.newsBar = e.target.value === 'true'; else delete cfg.newsBar;
                  done(await send(`/screen-groups/${g.id}`, { method: 'PATCH', body: JSON.stringify({ config: Object.keys(cfg).length ? cfg : null }) }), 'Lagret.'); }}>
                  <option value="">Standard (på)</option><option value="true">På</option><option value="false">Av</option>
                </select>)}</Field>
              <Field label="Lyd på direktesending" className="field half" note="Skjermene spiller en direktesending fra YouTube. Standard: uten lyd.">{(id, d) => (
                <select id={id} aria-describedby={d} defaultValue={g.config && typeof g.config.liveSound === 'boolean' ? String(g.config.liveSound) : ''} onChange={async (e) => {
                  const cfg = { ...(g.config || {}) }; if (e.target.value) cfg.liveSound = e.target.value === 'true'; else delete cfg.liveSound;
                  done(await send(`/screen-groups/${g.id}`, { method: 'PATCH', body: JSON.stringify({ config: Object.keys(cfg).length ? cfg : null }) }), 'Lagret.'); }}>
                  <option value="">Standard (av)</option><option value="true">På</option><option value="false">Av</option>
                </select>)}</Field>
            </div>
            <fieldset><legend>Skjermer i gruppen</legend>
              {all.map((s) => (
                <label key={s.id} className="check"><input type="checkbox" checked={s.group === g.id} onChange={async (e) => {
                  done(await post(`/screen-groups/${g.id}/screens`, e.target.checked ? { add: [s.id] } : { remove: [s.id] }), e.target.checked ? `${s.name} er lagt i ${g.name}.` : `${s.name} er tatt ut av ${g.name}.`); }} />
                  {' '}{s.name}{s.group && s.group !== g.id ? <span className="note"> (i en annen gruppe)</span> : null}</label>))}
            </fieldset>
            <TwoStep label="Slett gruppen" confirmLabel="Bekreft: slett gruppen" onConfirm={async () => {
              const r = await send(`/screen-groups/${g.id}`, { method: 'DELETE' }); done(r, r.ok ? r.body.message : ''); }} />
          </CardBody>
        </details>))}
      <CardBody>
        <FormDialog open={adding} title="Ny gruppe" dirty={Boolean(name.trim())} onClose={() => { setAdding(false); setName(''); }}>
          <div className="row inline-add">
            <Field label="Navn på gruppen" className="field half">{(id) => <input id={id} value={name} onChange={(e) => setName(e.target.value)} placeholder="F.eks. Inngangen" />}</Field>
            <Btn disabled={!name.trim()} onClick={async () => { const r = await post('/screen-groups', { name: name.trim() }); done(r, 'Gruppen er laget.'); if (r.ok) { setName(''); setAdding(false); } }}>Lag gruppe</Btn>
          </div>
          <Msg text={msg.text} tone={msg.tone} />
        </FormDialog>
      </CardBody>
    </Card>
  );
}
